import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { Save, Send, MessageSquare, CheckCircle2 } from "lucide-react";

const FIELDS = [
  { k: "name", label: "Kafe Adı" },
  { k: "phone", label: "Telefon" },
  { k: "hours", label: "Çalışma Saatleri" },
  { k: "instagram", label: "Instagram Linki" },
];

export default function CafeSettings() {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [tg, setTg] = useState({ telegramBotToken: "", telegramChatId: "", enabled: false });
  const [tgBusy, setTgBusy] = useState(false);
  const [testBusy, setTestBusy] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setF(r.data || {})).catch(() => {});
    api.get("/notify/settings").then((r) => setTg(r.data)).catch(() => {});
  }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    setBusy(true);
    try {
      const { key, _id, ...payload } = f;
      await api.put("/settings", payload);
      toast.success("Ayarlar kaydedildi");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const saveTg = async () => {
    setTgBusy(true);
    try {
      await api.put("/notify/settings", { telegramBotToken: tg.telegramBotToken || "", telegramChatId: tg.telegramChatId || "" });
      const { data } = await api.get("/notify/settings");
      setTg(data);
      toast.success("Telegram ayarları kaydedildi");
    } catch (e) { toast.error(errMsg(e)); } finally { setTgBusy(false); }
  };

  const testTg = async () => {
    setTestBusy(true);
    try {
      await api.post("/notify/test");
      toast.success("Test mesajı gönderildi! Telegram'ı kontrol edin.");
    } catch (e) { toast.error(errMsg(e)); } finally { setTestBusy(false); }
  };

  return (
    <div className="max-w-2xl">
      <h1 className="font-serif text-3xl sm:text-4xl text-white mb-1">Kafe Ayarları</h1>
      <p className="text-zinc-400 text-sm mb-6">Anasayfa ve menüde görünen bilgileri düzenleyin</p>

      <div className="card-lux p-6 space-y-4">
        {FIELDS.map((fld) => (
          <div key={fld.k}>
            <label className="text-xs font-mono uppercase tracking-widest text-zinc-400">{fld.label}</label>
            <input value={f[fld.k] || ""} onChange={set(fld.k)} className="lux-input mt-2" data-testid={`setting-${fld.k}`} />
          </div>
        ))}
        <div>
          <label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Adres</label>
          <textarea value={f.address || ""} onChange={set("address")} rows={2} className="lux-input mt-2" data-testid="setting-address" />
        </div>
        <div>
          <label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Hakkımızda</label>
          <textarea value={f.about || ""} onChange={set("about")} rows={3} className="lux-input mt-2" data-testid="setting-about" />
        </div>
        <div>
          <label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Harita Embed URL (Google Maps)</label>
          <input value={f.mapUrl || ""} onChange={set("mapUrl")} className="lux-input mt-2" data-testid="setting-mapurl" />
        </div>
        <button onClick={save} disabled={busy} className="btn-gold w-full py-3 rounded-xl flex items-center justify-center gap-2" data-testid="settings-save"><Save size={18} /> {busy ? "Kaydediliyor..." : "Kaydet"}</button>
      </div>

      {/* Telegram notifications */}
      <div className="card-lux p-6 mt-6" data-testid="telegram-card">
        <div className="flex items-center gap-3 mb-1">
          <div className="h-10 w-10 rounded-xl bg-[#229ED9]/15 border border-[#229ED9]/30 flex items-center justify-center"><Send size={20} className="text-[#5cc6f0]" /></div>
          <div>
            <h2 className="font-serif text-xl text-white leading-none">Telegram Sipariş Bildirimi</h2>
            <p className="text-xs text-zinc-400 mt-1">Yeni sipariş gelince telefonunuza anında mesaj düşer (ücretsiz)</p>
          </div>
          {tg.enabled && <span className="ml-auto flex items-center gap-1 text-xs text-emerald-400"><CheckCircle2 size={14} /> Aktif</span>}
        </div>

        <div className="rounded-xl bg-[#18181C] border border-[#D4AF37]/15 p-4 my-4 text-xs text-zinc-300 leading-relaxed space-y-1.5" data-testid="telegram-howto">
          <p className="text-[#FFE599] font-medium flex items-center gap-1.5"><MessageSquare size={13} /> Nasıl kurulur?</p>
          <p>1. Telegram'da <b>@BotFather</b>'a yazın → <b>/newbot</b> ile bir bot oluşturun, size verilen <b>Bot Token</b>'ı aşağıya yapıştırın.</p>
          <p>2. Oluşturduğunuz bota bir mesaj gönderin (veya bir gruba ekleyin). Ardından <b>@userinfobot</b>'a yazarak <b>Chat ID</b>'nizi öğrenip aşağıya yazın.</p>
          <p>3. Kaydedin ve “Test Mesajı Gönder” ile deneyin.</p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Bot Token</label>
            <input value={tg.telegramBotToken || ""} onChange={(e) => setTg({ ...tg, telegramBotToken: e.target.value })} className="lux-input mt-2 font-mono text-sm" placeholder="123456789:ABCdef..." data-testid="tg-token" />
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Chat ID</label>
            <input value={tg.telegramChatId || ""} onChange={(e) => setTg({ ...tg, telegramChatId: e.target.value })} className="lux-input mt-2 font-mono text-sm" placeholder="Örn: 123456789" data-testid="tg-chatid" />
          </div>
          <div className="flex gap-3">
            <button onClick={saveTg} disabled={tgBusy} className="btn-gold flex-1 py-3 rounded-xl flex items-center justify-center gap-2" data-testid="tg-save"><Save size={18} /> {tgBusy ? "..." : "Kaydet"}</button>
            <button onClick={testTg} disabled={testBusy || !tg.enabled} className="btn-outline flex-1 py-3 rounded-xl flex items-center justify-center gap-2" data-testid="tg-test"><Send size={18} /> {testBusy ? "..." : "Test Mesajı Gönder"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
